import { useRef, useState, useCallback } from 'react';

/**
 * Pull-to-refresh-hook for mobil.
 * Brukes på en scroll-container.
 */
export function usePullToRefresh(onRefresh: () => void | Promise<void>) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);

  const TOUCH_THRESHOLD = 5;
  const MAX_PULL = 80;
  const REFRESH_THRESHOLD = 60;

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const el = e.currentTarget as HTMLElement;
      if (el.scrollTop === 0) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
      } else {
        pullingRef.current = false;
      }
    },
    [],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pullingRef.current) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy > TOUCH_THRESHOLD) {
        // Motstått scroll
        const distance = Math.min(dy * 0.5, MAX_PULL);
        setPullDistance(distance);
      }
    },
    [],
  );

  const onTouchEnd = useCallback(async () => {
    if (!pullingRef.current) return;
    pullingRef.current = false;

    if (pullDistance >= REFRESH_THRESHOLD) {
      setRefreshing(true);
      setPullDistance(40);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, onRefresh]);

  return {
    pullDistance,
    refreshing,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
